import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Form, Button, Table, Select, DatePicker, Space, Card, Row, Col, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../constants/api';
import './Reports.css';

const { RangePicker } = DatePicker;

const MaterialMovementReport = () => {
  const [form] = Form.useForm();
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [basicAmount, setBasicAmount] = useState(0);
  const [searchParams] = useSearchParams();

  // Default dates: start of current month to today
  const defaultStartDate = dayjs().startOf('month');
  const defaultEndDate = dayjs();

  useEffect(() => {
    fetchMaterials();
    form.setFieldsValue({
      dateRange: [defaultStartDate, defaultEndDate]
    });
  }, []);

  useEffect(() => {
    if (materials.length > 0) {
      const materialIdParam = searchParams.get('materialId');
      if (materialIdParam) {
        const materialId = parseInt(materialIdParam, 10);
        const materialExists = materials.some(mat => mat.id === materialId);
        if (materialExists) {
          form.setFieldsValue({ materialId });
          handleSearch();
        }
      }
    }
  }, [materials, searchParams, form]);

  const fetchMaterials = async () => {
    setLoadingMaterials(true);
    try {
      const response = await axios.get(`${API_BASE_URL}Material/List`);
      if (response.data.code === 0) {
        setMaterials(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      const { materialId, dateRange } = values;

      if (!materialId || !dateRange || dateRange.length !== 2) {
        return;
      }

      setLoadingReport(true);

      const response = await axios.get(`${API_BASE_URL}Reports/ReportMaterialMovement`, {
        params: {
          materialId,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        }
      });

      if (response.data.code === 0) {
        setReportData(response.data.data || []);
        setBasicAmount(response.data.basicAmount || 0);
      }
    } catch (error) {
      console.error('Report fetch error:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  const tableColumns = [
    {
      title: 'التاريخ',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('YYYY/MM/DD')
    },
    {
      title: 'اسم المستودع',
      dataIndex: 'repoName',
      key: 'repoName'
    },
    {
      title: 'الكمية داخلة',
      dataIndex: 'quantityIn',
      key: 'quantityIn',
      render: (val) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{Number(val).toLocaleString()}</span>
    },
    {
      title: 'الكمية خارجة',
      dataIndex: 'quantityOut',
      key: 'quantityOut',
      render: (val) => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{Number(val).toLocaleString()}</span>
    }
  ];

  const totalQuantityIn = reportData.reduce((acc, item) => acc + (item.quantityIn || 0), 0);
  const totalQuantityOut = reportData.reduce((acc, item) => acc + (item.quantityOut || 0), 0);

  return (
    <div className="report-container">
      <Card size="small" className="report-filter-card">
        <Form form={form} layout="inline" className="report-filter-form">
          <Form.Item name="materialId" label="المادة" rules={[{ required: true, message: 'يرجى اختيار المادة' }]} className="report-form-item">
            <Select
              placeholder="اختر المادة"
              loading={loadingMaterials}
              showSearch
              allowClear
              size="small"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={materials.map(mat => ({
                label: `${mat.name} ${mat.unit ? ` [${mat.unit}]` : ''}`,
                value: mat.id
              }))}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="الفترة" rules={[{ required: true, type: 'array', min: 2, message: 'يرجى اختيار الفترة' }]} className="report-form-item">
            <RangePicker
              format="YYYY/MM/DD"
              defaultValue={[defaultStartDate, defaultEndDate]}
              size="small"
            />
          </Form.Item>
          <Form.Item className="report-form-item report-btn-item">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loadingReport}
              size="small"
            >
              تنفيذ
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {reportData.length > 0 && (
        <>
          <Card size="small" className="report-stats-card">
            <Row gutter={[8, 8]}>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-blue">
                  <div className="stat-label">الرصيد السابق</div>
                  <div className="stat-value">{basicAmount.toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-green">
                  <div className="stat-label">إجمالي الداخل</div>
                  <div className="stat-value">{totalQuantityIn.toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-red">
                  <div className="stat-label">إجمالي الخارج</div>
                  <div className="stat-value">{totalQuantityOut.toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-orange">
                  <div className="stat-label">صافي الرصيد</div>
                  <div className="stat-value">{(totalQuantityIn - totalQuantityOut + basicAmount).toLocaleString()}</div>
                </div>
              </Col>
            </Row>
          </Card>

          <Card size="small" className="report-table-card">
            <Spin spinning={loadingReport}>
              <Table
                columns={tableColumns}
                dataSource={reportData}
                rowKey={(record, index) => `${record.date}-${record.repoName}-${index}`}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            </Spin>
          </Card>
        </>
      )}
    </div>
  );
};

export default MaterialMovementReport;
